/**
 * Connections API — provider logins at /api/finance/connections.
 *
 * Providers are ports: Plaid (bank OAuth), SimpleFIN (claimed access URL),
 * Teller (mTLS enrollment), manual (the null connector). The browser only
 * ever sees the sanitized ConnectionResponse — tokens, cursors, and item
 * ids never cross the wire.
 */

import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  Connection,
  ConnectionHealth,
  EnqueuedResponse,
  PlaidLinkTokenResponse,
  SpendGroupBy,
  SpendResponse,
  RecurringResponse,
} from "../types";

const BASE = "/api/finance/connections";

export function listConnections(): Promise<Connection[]> {
  return apiClient<Connection[]>(BASE);
}

export function getConnectionHealth(): Promise<ConnectionHealth[]> {
  return apiClient<ConnectionHealth[]>(`${BASE}/health`);
}

/**
 * Manually trigger a sync outside the scheduled sweep. Idempotent — the dedup
 * index makes double-triggering converge. Returns 202 + the job envelope;
 * the durable truth is the connection row, not the job.
 */
export function triggerSync(connectionId: string): Promise<EnqueuedResponse> {
  return apiClient<EnqueuedResponse>(`${BASE}/${connectionId}/sync`, {
    method: "POST",
  });
}

// ===================
// Plaid — bank-hosted OAuth via Plaid Link
// ===================

export function createPlaidLinkToken(): Promise<PlaidLinkTokenResponse> {
  return apiClient<PlaidLinkTokenResponse>(`${BASE}/plaid/link-token`, {
    method: "POST",
  });
}

/** Exchange the 30-minute public_token for a durable connection. */
export function exchangePlaidPublicToken(args: {
  publicToken: string;
  institutionName: string | null;
  institutionId: string | null;
}): Promise<Connection> {
  return apiClient<Connection>(`${BASE}/plaid/exchange`, {
    method: "POST",
    body: JSON.stringify({
      public_token: args.publicToken,
      institution_name: args.institutionName,
      institution_id: args.institutionId,
    }),
  });
}

// ===================
// SimpleFIN — one-time setup token claimed server-side
// ===================

export function claimSimpleFinToken(setupToken: string): Promise<Connection> {
  return apiClient<Connection>(`${BASE}/simplefin/claim`, {
    method: "POST",
    body: JSON.stringify({ setup_token: setupToken }),
  });
}

// ===================
// Analysis (colocated here to keep api/ at six modules; spend/recurring
// are read-only aggregates with no feature state of their own)
// ===================

export function getSpend(params: {
  group_by: SpendGroupBy;
  since?: string;
  until?: string;
  account?: string;
}): Promise<SpendResponse> {
  const qs = buildQueryString({
    group_by: params.group_by,
    since: params.since,
    until: params.until,
    account: params.account,
  });
  return apiClient<SpendResponse>(`/api/finance/analysis/spend${qs}`);
}

export function getRecurring(): Promise<RecurringResponse> {
  return apiClient<RecurringResponse>(`/api/finance/analysis/recurring`);
}
